
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Lock, Mail, KeyRound, Shield, AlertCircle, ShieldAlert } from 'lucide-react';
import { Logo } from './Logo';
import { GlowingEffect } from './GlowingEffect';
import { UserRole } from '../../App';
import { supabase } from '../../lib/supabase';

interface AuthPageProps {
  onBack: () => void;
  onSuccess: (role: UserRole) => void;
  onSimulateAccessGate: () => void;
}

// Master Credentials as requested
const MASTER_EMAIL = "zachmiles413@gmail.com";
const MASTER_PASSWORD = "Shawnmendes999";

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <div className="relative rounded-full">
      <GlowingEffect
        spread={25}
        glow={true}
        disabled={false}
        proximity={50}
        inactiveZone={0.01}
        borderWidth={1}
      />
      <button 
        onClick={onClick}
        className="relative flex items-center gap-2 rounded-full border border-border/50 bg-background/50 px-4 py-2 backdrop-blur-md transition-all group outline-none hover:bg-muted/50"
      >
        <ArrowLeft size={14} className="text-muted-foreground transition-transform group-hover:-translate-x-1" />
        <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-muted-foreground group-hover:text-foreground">
          Go Back
        </span>
      </button>
    </div>
  );
}

export function AuthPage({ onBack, onSuccess }: AuthPageProps) {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const enteredEmail = formData.email.toLowerCase().trim();
    const enteredPassword = formData.password;

    // Strict Master Check
    if (enteredEmail !== MASTER_EMAIL || enteredPassword !== MASTER_PASSWORD) {
      setTimeout(() => {
        setError("Access Blocked: These credentials do not match the Master Agency Owner file.");
        setIsLoading(false);
      }, 1000);
      return;
    }

    try {
      // We still attempt a Supabase login to keep the session alive if the user is in the DB
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: enteredEmail,
        password: enteredPassword,
      });

      // If Supabase fails (e.g. user not in DB yet), we still allow access 
      // because these are the MASTER strings requested by the user.
      if (authError && authError.message !== "Invalid login credentials") {
         console.warn("Supabase Sync Error:", authError.message);
      }

      // Hardcoded Success for the Owner
      setTimeout(() => {
        onSuccess('agency_owner');
      }, 1200);

    } catch (err: any) {
      // Fallback: If network fails, still grant access because the master strings matched
      onSuccess('agency_owner');
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }}
      className="bg-background min-h-screen flex flex-col items-center px-6 relative overflow-hidden"
    >
      {/* Background Aesthetic */}
      <div className="absolute top-[-10%] left-[-10%] size-[600px] bg-brand-purple/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] size-[600px] bg-brand-magenta/5 blur-[120px] rounded-full pointer-events-none" />

      <motion.header 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-7xl flex items-center justify-between py-10 z-30"
      >
        <Logo />
        <div className="flex items-center gap-4">
          <BackButton onClick={onBack} />
        </div>
      </motion.header>

      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-md pb-24 relative z-10">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full space-y-10"
        >
          <div className="text-center space-y-4">
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 mb-2"
            >
              <ShieldAlert className="size-3 text-primary" />
              <span className="text-[9px] font-bold tracking-[0.4em] uppercase text-primary">Owner Verified Access</span>
            </motion.div>
            <h1 className="text-4xl md:text-5xl font-light tracking-tight text-foreground">
              Hello, Zach
            </h1>
            <p className="text-sm text-muted-foreground font-light max-w-xs mx-auto leading-relaxed text-center">
              Please enter your master agency email and secret password to unlock your private dashboard.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-5">
              {error && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-4 rounded-2xl bg-destructive/10 border border-destructive/20 flex items-start gap-3"
                >
                  <AlertCircle className="size-4 text-destructive shrink-0 mt-0.5" />
                  <p className="text-xs text-destructive leading-relaxed font-medium">{error}</p>
                </motion.div>
              )}

              <div className="space-y-2">
                <label className="text-[9px] font-bold tracking-[0.4em] uppercase text-muted-foreground/40 ml-1">Your Email</label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground/30 group-focus-within:text-foreground transition-colors"><Mail size={16} /></div>
                  <input 
                    type="email" 
                    required 
                    placeholder="Enter zachmiles413@gmail.com" 
                    value={formData.email} 
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                    className="w-full bg-muted/5 border border-border/40 rounded-2xl py-4 pl-12 pr-6 outline-none focus:border-foreground/40 transition-all text-sm font-light text-foreground placeholder:text-muted-foreground/10" 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[9px] font-bold tracking-[0.4em] uppercase text-muted-foreground/40 ml-1">Your Secret Password</label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground/30 group-focus-within:text-foreground transition-colors"><KeyRound size={16} /></div>
                  <input 
                    type="password" 
                    required 
                    placeholder="Enter your private password" 
                    value={formData.password} 
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })} 
                    className="w-full bg-muted/5 border border-border/40 rounded-2xl py-4 pl-12 pr-6 outline-none focus:border-foreground/40 transition-all text-sm font-light text-foreground placeholder:text-muted-foreground/10" 
                  />
                </div>
              </div>
            </div>

            <div className="pt-6 flex flex-col items-center gap-8">
              <button
                type="submit"
                disabled={isLoading}
                className="group/btn relative w-full py-5 px-6 rounded-[24px] text-[11px] font-bold tracking-[0.4em] uppercase transition-all duration-500 flex items-center justify-center gap-3 bg-foreground text-background hover:scale-[0.98] shadow-2xl shadow-foreground/5 disabled:opacity-50"
              >
                <GlowingEffect spread={60} glow={true} disabled={false} proximity={120} inactiveZone={0.01} borderWidth={2} />
                <span className="relative z-10">
                  {isLoading ? 'Verifying...' : 'Unlock Agency Dashboard'}
                </span>
                {!isLoading && <ArrowRight size={14} className="relative z-10 transition-transform group-hover/btn:translate-x-1" />}
              </button>

              <div className="flex items-center gap-4">
                <div className="flex -space-x-1.5">
                  <div className="size-5 rounded-full border border-background bg-muted overflow-hidden ring-1 ring-border/20">
                    <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Zach" alt="Zach" />
                  </div>
                </div>
                <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground/50">Exclusive Owner Portal</span>
              </div>
            </div>
          </form>
        </motion.div>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
        className="w-full max-w-7xl mx-auto px-6 py-10 border-t border-border/10 flex flex-col sm:flex-row justify-between items-center gap-4 z-10"
      >
        <div className="flex items-center gap-2">
          <Lock className="size-3 text-primary" />
          <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-muted-foreground/30">AES-256 Agency Encryption</span>
        </div>
        <div className="flex gap-8 text-muted-foreground/40">
           <p className="text-[9px] font-bold uppercase tracking-widest">Aure OS Version 2.4.0</p>
        </div>
      </motion.div>
    </motion.div>
  );
}
